import { Search, Settings, HelpCircle, Mail } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useUser } from "../../context/UserContext";
import { useTournaments, useUserInvitations } from "../../hooks/useQueries";
import { logoGreen24 } from "../../constants/icons";
import "./styles.css";

interface RecentTournament {
  id: string;
  name: string;
  accessedAt: number;
}

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [recentTournaments, setRecentTournaments] = useState<RecentTournament[]>([]);

  // Use shared React Query hooks (deduplicates with other components)
  const { data: tournamentsResponse } = useTournaments({ page: 1, limit: 100 });
  const { data: invitations } = useUserInvitations();

  const pendingInvitationsCount = useMemo(() => {
    if (!invitations) return 0;
    return invitations.filter(
      (inv) => !inv.accepted && !inv.declined && !inv.isExpired
    ).length;
  }, [invitations]);

  // Get user initials from name
  const getInitials = (name: string) => {
    if (!name) return "JD";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get tournament abbreviation: up to 2 letters for 1–2 words, up to 3 letters for 3+ words
  const getTournamentInitial = (name: string) => {
    if (!name) return "T";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 3) {
      return parts
        .slice(0, 3)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();
    }
    if (parts.length === 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.trim().substring(0, 2).toUpperCase();
  };

  const userInitials = user ? getInitials(user.name) : "JD";

  // Validate recent tournaments against the fetched tournaments list
  useEffect(() => {
    const stored = localStorage.getItem("recentTournaments");
    if (!stored) return;

    let tournaments: RecentTournament[] = [];
    try {
      tournaments = JSON.parse(stored);
      if (!Array.isArray(tournaments)) {
        tournaments = [tournaments];
      }
    } catch {
      tournaments = [];
    }

    if (tournaments.length === 0) return;

    if (tournamentsResponse?.data) {
      const validTournamentIds = new Set(tournamentsResponse.data.map((t) => t.id));
      const validTournaments = tournaments
        .filter((t) => validTournamentIds.has(t.id))
        .slice(0, 2);

      if (validTournaments.length !== tournaments.length) {
        localStorage.setItem("recentTournaments", JSON.stringify(validTournaments));
      }

      setRecentTournaments(validTournaments);
    } else {
      // If tournaments data not yet loaded, show stored ones
      setRecentTournaments(tournaments.slice(0, 2));
    }
  }, [tournamentsResponse]);

  // Listen for tournament updates to re-validate
  useEffect(() => {
    const handleTournamentUpdated = () => {
      const stored = localStorage.getItem("recentTournaments");
      if (!stored) return;
      try {
        let tournaments: RecentTournament[] = JSON.parse(stored);
        if (!Array.isArray(tournaments)) tournaments = [tournaments];
        setRecentTournaments(tournaments.slice(0, 2));
      } catch {
        // ignore
      }
    };

    window.addEventListener("tournamentUpdated", handleTournamentUpdated);
    return () => window.removeEventListener("tournamentUpdated", handleTournamentUpdated);
  }, []);

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const handleSearchClick = () => {
    console.log("Search clicked");
    navigate("/dashboard/search");
  };

  const handleSettingsClick = () => {
    console.log("Settings clicked");
    navigate("/dashboard/settings");
  };

  const handleHelpClick = () => {
    console.log("Help clicked");
    navigate("/dashboard/help");
  };

  const handleInvitationsClick = () => {
    navigate("/dashboard/invitations");
  };

  const handleUserAvatarClick = () => {
    console.log("User avatar clicked");
    navigate("/dashboard/profile");
  };

  const handleTournamentClick = (tournamentId: string) => {
    const currentTournamentId = location.pathname.match(/\/dashboard\/tournament\/([^/]+)/)?.[1];
    if (currentTournamentId === tournamentId) {
      return;
    }
    navigate(`/dashboard/tournament/${encodeURIComponent(tournamentId)}/home`);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="sidebar">
      <div className="sidebar-left">
        {/* Logo */}
        <div
          className={`sidebar-logo ${isActive("/dashboard") ? "sidebar-logo--active" : ""}`}
          onClick={handleLogoClick}
        >
          <img src={logoGreen24} alt="OneLeague" className="logo-icon" />
        </div>

        {/* Recent Tournaments (up to 2) */}
        {recentTournaments.map((tournament) => {
          const currentTournamentId = location.pathname.match(/\/dashboard\/tournament\/([^/]+)/)?.[1];
          const isActive = currentTournamentId === tournament.id;
          return (
            <button
              key={tournament.id}
              className={`sidebar-tournament ${isActive ? "sidebar-tournament--active" : ""}`}
              onClick={() => handleTournamentClick(tournament.id)}
              title={tournament.name}
            >
              <div className="sidebar-tournament-icon-wrapper">
                <p className="sidebar-tournament-icon-text">{getTournamentInitial(tournament.name)}</p>
              </div>
            </button>
          );
        })}

        <div className="sidebar-divider">
          <div className="sidebar-divider-line" />
        </div>

        {/* Navigation Icons */}
        <nav className="sidebar-nav">
          <button
            className={`nav-icon ${isActive("/dashboard/search") ? "nav-icon--active" : ""}`}
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <Search size={20} color="#D1D1D1" strokeWidth={1} />
          </button>
          <button
            className={`nav-icon ${isActive("/dashboard/invitations") ? "nav-icon--active" : ""}`}
            onClick={handleInvitationsClick}
            aria-label="Invitations"
            style={{ position: "relative" }}
          >
            <Mail size={20} color="#D1D1D1" strokeWidth={1} />
            {pendingInvitationsCount > 0 && <span className="nav-icon-badge">{pendingInvitationsCount}</span>}
          </button>
          <button
            className={`nav-icon ${isActive("/dashboard/settings") ? "nav-icon--active" : ""}`}
            onClick={handleSettingsClick}
            aria-label="Settings"
          >
            <Settings size={20} color="#D1D1D1" strokeWidth={1} />
          </button>
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="sidebar-bottom">
        <button
          className={`nav-icon ${isActive("/dashboard/help") ? "nav-icon--active" : ""}`}
          onClick={handleHelpClick}
          aria-label="Help"
        >
          <HelpCircle size={20} color="#D1D1D1" strokeWidth={1} />
        </button>
        <button
          className="user-avatar"
          onClick={handleUserAvatarClick}
          title={user?.name || "User"}
          aria-label="User profile"
        >
          {userInitials}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
